/**
 * Presentation formatting helpers for nutritional values.
 *
 * CONTRACT:
 *   - All functions receive raw engine numbers (full precision) and return
 *     human-readable strings suitable for display.
 *   - NO mutation of engine data; these are pure display helpers.
 *   - No scientific notation is ever emitted (e.g. -4.5e-13 → "0").
 *   - Values with |x| < 0.5 round to zero and are displayed as "0".
 */

/**
 * Clamps a value that is effectively zero (floating-point epsilon) to exactly 0.
 * This prevents display of values like -4.547473508864641e-13.
 */
function clampEpsilon(n: number): number {
  return Math.abs(n) < 0.5 ? 0 : Math.round(n);
}

/**
 * Formats a gram value for display (integer, no unit).
 * Example: 98.71493066145078 → "99"
 */
export function formatGrams(n: number): string {
  return String(clampEpsilon(n));
}

/**
 * Formats a kilocalorie value for display (integer, no unit).
 * Example: 2083.999 → "2084"
 */
export function formatKcal(n: number): string {
  return String(clampEpsilon(n));
}

/**
 * Formats a macro nutrient value (protein / carbs / fat) for display (integer, no unit).
 * Example: 77.8256279308294 → "78"
 */
export function formatMacro(n: number): string {
  return String(clampEpsilon(n));
}

/**
 * Formats a convergence delta value for display, including sign prefix.
 * - Zero (or near-zero epsilon < |0.5|): returns "0"
 * - Positive: returns "+N" (e.g. "+78")
 * - Negative: returns "-N" (e.g. "-13")
 *
 * Example: -4.547473508864641e-13 → "0"
 * Example: 77.8256279308294 → "+78"
 * Example: -12.3 → "-12"
 */
export function formatDelta(n: number): string {
  const rounded = clampEpsilon(n);
  if (rounded === 0) return "0";
  if (rounded > 0) return `+${rounded}`;
  return String(rounded);
}
